<template>
    <div class="rcv-page">
        <!-- Toolbar -->
        <div class="rcv-toolbar">
            <div class="rcv-toolbar__left">
                <h3 class="rcv-toolbar__title">
                    <i class="el-icon-document-copy"></i> Quản lý hồ sơ ứng viên
                </h3>
                <span class="rcv-toolbar__sub">
                    Tiếp nhận, tra cứu hồ sơ và xem CV ứng tuyển theo từng yêu cầu tuyển dụng
                </span>
                <el-tag
                    v-if="activeJobInfo"
                    size="small"
                    type="primary"
                    closable
                    class="rcv-active-job-tag"
                    @close="clearJobFilter"
                >
                    <i class="el-icon-s-opportunity"></i> Đang xem yêu cầu #{{ activeJobInfo.id }}: {{ activeJobInfo.title || activeJobInfo.position_title }}
                </el-tag>
            </div>
            <div class="rcv-toolbar__right">
                <el-button
                    size="small"
                    icon="el-icon-refresh"
                    :loading="rcvLoading || roLoading"
                    @click="refreshData"
                >
                    Làm mới
                </el-button>
                <el-button
                    size="small"
                    type="primary"
                    icon="el-icon-plus"
                    @click="openCreate"
                >
                    Thêm hồ sơ mới
                </el-button>
            </div>
        </div>

        <!-- Thẻ thống kê tổng quan tiến trình -->
        <div class="rcv-stats">
            <div class="rcv-stat-card">
                <div class="rcv-stat-card__icon rcv-stat-card__icon--primary">
                    <i class="el-icon-user"></i>
                </div>
                <div class="rcv-stat-card__info">
                    <div class="rcv-stat-card__value">{{ totalCount }}</div>
                    <div class="rcv-stat-card__label">Tổng hồ sơ ứng viên</div>
                </div>
            </div>

            <div class="rcv-stat-card">
                <div class="rcv-stat-card__icon rcv-stat-card__icon--warning">
                    <i class="el-icon-time"></i>
                </div>
                <div class="rcv-stat-card__info">
                    <div class="rcv-stat-card__value">{{ pendingReviewCount }}</div>
                    <div class="rcv-stat-card__label">Cần duyệt hồ sơ</div>
                </div>
            </div>

            <div class="rcv-stat-card">
                <div class="rcv-stat-card__icon rcv-stat-card__icon--info">
                    <i class="el-icon-chat-dot-round"></i>
                </div>
                <div class="rcv-stat-card__info">
                    <div class="rcv-stat-card__value">{{ interviewingCount }}</div>
                    <div class="rcv-stat-card__label">Đang phỏng vấn</div>
                </div>
            </div>

            <div class="rcv-stat-card">
                <div class="rcv-stat-card__icon rcv-stat-card__icon--success">
                    <i class="el-icon-trophy"></i>
                </div>
                <div class="rcv-stat-card__info">
                    <div class="rcv-stat-card__value">{{ hiredCount }}</div>
                    <div class="rcv-stat-card__label">Đã nhận việc</div>
                </div>
            </div>
        </div>

        <!-- Bộ lọc -->
        <div class="rcv-card rcv-filter-card">
            <cv-filter-bar
                v-model="filters"
                :orders="roOrders"
                @change="onFilterChange"
            />
        </div>

        <!-- Bảng danh sách ứng viên -->
        <div class="rcv-card">
            <cv-table
                :cv-list="displayedCvs"
                :loading="rcvLoading"
                :orders="roOrders"
                :is-hr-department="isHrDepartment"
                @view="openDetail"
                @preview-cv="openPreviewCv"
                @edit="openEdit"
                @delete="handleDelete"
                @open-preliminary="openPreliminary"
                @open-interview="openInterview"
                @cancel-interview="handleCancelInterview"
                @open-probation="openProbation"
                @open-schedule="openSchedule"
                @open-offer="openOffer"
                @open-work-confirmation="openWorkConfirmation"
                @open-training-evaluation="openTrainingEvaluation"
                @mark-hired="handleMarkHired"
            />

            <!-- Phân trang -->
            <div v-if="totalFilteredCount > 0" class="rcv-pagination">
                <el-pagination
                    background
                    layout="total, sizes, prev, pager, next, jumper"
                    :total="totalFilteredCount"
                    :page-size="pagination.limit"
                    :page-sizes="[10, 15, 20, 50, 100]"
                    :current-page="pagination.page"
                    @current-change="handlePageChange"
                    @size-change="handleSizeChange"
                />
            </div>
        </div>

        <!-- Dialog Chi tiết hồ sơ & Xem CV PDF -->
        <cv-detail-dialog
            ref="detailDialogRef"
            :visible.sync="detailDialogVisible"
            :candidate="selectedCandidate"
            :orders="roOrders"
            :initial-tab="detailInitialTab"
            :is-hr-department="isHrDepartment"
            :submitting="statusSubmitting"
            @edit="openEdit"
            @update-status="handleUpdateCandidateStatus"
            @open-preliminary="openPreliminary"
            @open-interview="openInterview"
            @open-probation="openProbation"
            @open-offer="openOffer"
            @open-work-confirmation="openWorkConfirmation"
            @open-training-evaluation="openTrainingEvaluation"
        />

        <!-- Dialog Gửi Duyệt CV: Chuyển Teamlead duyệt -->
        <preliminary-review-dialog
            :visible.sync="preliminaryDialogVisible"
            :candidate="selectedPreliminaryCandidate"
            :orders="roOrders"
            :submitting="statusSubmitting"
            @submit="handlePreliminarySubmit"
        />

        <!-- Dialog Điền kết quả Phỏng vấn V1 & V2 (Teamlead) -->
        <interview-evaluation-dialog
            :visible.sync="interviewDialogVisible"
            :candidate="selectedInterviewCandidate"
            :orders="roOrders"
            :round="selectedInterviewRound"
            :is-hr-department="isHrDepartment"
            :current-user-name="currentUserName"
            :submitting="statusSubmitting"
            @submit="handleInterviewSubmit"
            @cancel-interview="handleCancelInterview"
        />

        <!-- Dialog Điền kết quả Đánh giá Thử việc (Teamlead) -->
        <probation-evaluation-dialog
            :visible.sync="probationDialogVisible"
            :candidate="selectedProbationCandidate"
            :orders="roOrders"
            :is-hr-department="isHrDepartment"
            :submitting="statusSubmitting"
            @submit="handleProbationSubmit"
        />

        <!-- Dialog Điền thông tin Thử việc & Offer: Cần làm thủ tục Pass (#14) -->
        <offer-procedure-dialog
            :visible.sync="offerProcedureDialogVisible"
            :candidate="selectedOfferCandidate"
            :orders="roOrders"
            :is-hr-department="isHrDepartment"
            :submitting="statusSubmitting"
            @submit="handleOfferProcedureSubmit"
        />

        <!-- Dialog Xác nhận ứng viên đi làm / không đi làm (HR) -->
        <work-confirmation-dialog
            :visible.sync="workConfirmationDialogVisible"
            :candidate="selectedWorkCandidate"
            :orders="roOrders"
            :is-hr-department="isHrDepartment"
            :initial-has-work="initialWorkStatus"
            :submitting="statusSubmitting"
            @submit="handleWorkConfirmationSubmit"
        />

        <!-- Dialog Nhập kết quả Đào tạo hội nhập (HR) cho Tuyển chính thức -->
        <training-evaluation-dialog
            :visible.sync="trainingEvaluationDialogVisible"
            :candidate="selectedTrainingCandidate"
            :orders="roOrders"
            :is-hr-department="isHrDepartment"
            :submitting="statusSubmitting"
            @submit="handleTrainingEvaluationSubmit"
        />

        <!-- Dialog Thêm mới / Chỉnh sửa hồ sơ -->
        <cv-form-dialog
            :visible.sync="formDialogVisible"
            :candidate-data="selectedEditCandidate"
            :orders="roOrders"
            :default-job-id="activeDefaultJobId"
            :submitting="submitting"
            @submit="handleFormSubmit"
        />
    </div>
</template>

<script>
import { mapGetters } from "vuex";
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import {
    RCV_GET_CVS,
    RCV_CREATE_CV,
    RCV_UPDATE_CV,
    RCV_DELETE_CV,
    RCV_UPDATE_CV_STATUS,
    RCV_GET_CV_STATUS,
    RCV_UPSERT_CV,
} from "@/core/services/store/recruitment-cvs/recruitmentCvs.module";
import { RO_GET_ORDERS } from "@/core/services/store/recruitment-orders/recruitmentOrders.module";
import {
    HR_CAN_DUYET,
    HR_TU_CHOI,
    TEAM_LEAD_CAN_DUYET,
    TEAM_LEAD_TU_CHOI,
    PHONG_VAN_V1,
    HUY_PHONG_VAN_V1,
    PHONG_VAN_V2,
    HUY_PHONG_VAN_V2,
    DA_NHAP_KET_QUA_PHONG_VAN_V1_PASS,
    DA_NHAP_KET_QUA_PHONG_VAN_V2_PASS,
    CAN_DIEN_OFFER,
    CAN_LAM_THU_TUC_PASS,
    UNG_VIEN_KHONG_DI_LAM,
    DA_GUI_DAO_TAO_HOI_NHAP,
    CAN_DANH_GIA_THU_VIEC,
    DA_NHAP_KET_QUA_DANH_GIA_THU_VIEC_PASS,
    DA_NHAP_KET_QUA_DANH_GIA_THU_VIEC_FAIL,
    DA_NHAN_VIEC,
    STATUS_TEXT,
} from "@/core/config/recruitmentCvs/recruitmentCvsOption";
import CvFilterBar from "./components/CvFilterBar.vue";
import CvTable from "./components/CvTable.vue";
import CvDetailDialog from "./components/CvDetailDialog.vue";
import CvFormDialog from "./components/CvFormDialog.vue";
import InterviewEvaluationDialog from "./components/InterviewEvaluationDialog.vue";
import ProbationEvaluationDialog from "./components/ProbationEvaluationDialog.vue";
import PreliminaryReviewDialog from "./components/PreliminaryReviewDialog.vue";
import OfferProcedureDialog from "./components/OfferProcedureDialog.vue";
import WorkConfirmationDialog from "./components/WorkConfirmationDialog.vue";
import TrainingEvaluationDialog from "./components/TrainingEvaluationDialog.vue";

export default {
    name: "RecruitmentCvs",
    components: {
        CvFilterBar,
        CvTable,
        CvDetailDialog,
        CvFormDialog,
        InterviewEvaluationDialog,
        ProbationEvaluationDialog,
        PreliminaryReviewDialog,
        OfferProcedureDialog,
        WorkConfirmationDialog,
        TrainingEvaluationDialog,
    },
    data() {
        return {
            filters: {
                keyword: "",
                job_id: "",
                type: "",
                status: "",
                person_in_charge_id: "",
                teamlead_id: "",
                date_range: null,
            },
            pagination: {
                page: 1,
                limit: 20,
            },

            detailDialogVisible: false,
            detailInitialTab: "info",
            selectedCandidate: null,

            preliminaryDialogVisible: false,
            selectedPreliminaryCandidate: null,

            interviewDialogVisible: false,
            selectedInterviewRound: 1,
            selectedInterviewCandidate: null,

            probationDialogVisible: false,
            selectedProbationCandidate: null,

            offerProcedureDialogVisible: false,
            selectedOfferCandidate: null,

            workConfirmationDialogVisible: false,
            selectedWorkCandidate: null,
            initialWorkStatus: true,

            trainingEvaluationDialogVisible: false,
            selectedTrainingCandidate: null,

            formDialogVisible: false,
            selectedEditCandidate: null,
            submitting: false,
            statusSubmitting: false,
        };
    },
    computed: {
        ...mapGetters(["rcvList", "rcvPagination", "rcvLoading", "roOrders", "roLoading", "currentUser"]),
        myDepartmentIds() {
            const ids = new Set();
            const u = this.currentUser || {};
            const push = (v) => {
                const n = Number(v);
                if (n) ids.add(n);
            };
            if (u.department_id) push(u.department_id);
            if (u.profile && u.profile.department_id) push(u.profile.department_id);
            if (u.department) {
                push(u.department.id);
                push(u.department.department_id);
            }
            const ud = u.use_department;
            const digOne = (d) => {
                if (!d || typeof d !== "object") return;
                push(d.department_id);
                push(d.id);
                if (d.department) {
                    push(d.department.id);
                    push(d.department.department_id);
                }
            };
            if (Array.isArray(ud)) ud.forEach(digOne);
            else digOne(ud);
            return [...ids];
        },
        isHrDepartment() {
            return this.myDepartmentIds.includes(3);
        },
        currentUserName() {
            const u = this.currentUser || {};
            return u.name || u.full_name || u.username || "";
        },
        currentDepartmentId() {
            const u = this.currentUser || {};
            if (u.department_id) return u.department_id;
            if (u.profile && u.profile.department_id) return u.profile.department_id;
            if (u.department && u.department.id) return u.department.id;
            if (Array.isArray(u.use_department) && u.use_department.length > 0) {
                return u.use_department[0].department_id || u.use_department[0].id || null;
            }
            if (u.use_department && typeof u.use_department === "object") {
                return u.use_department.department_id || u.use_department.id || null;
            }
            return null;
        },
        activeJobInfo() {
            if (!this.filters.job_id) return null;
            const filterJob = String(this.filters.job_id);
            const queryOrderId = this.$route.query.order_id ? String(this.$route.query.order_id) : "";
            return (this.roOrders || []).find((o) =>
                (queryOrderId && String(o.id) === queryOrderId) ||
                (o.recruitment_job_id && String(o.recruitment_job_id) === filterJob) ||
                String(o.id) === filterJob ||
                (o.job_id && String(o.job_id) === filterJob)
            ) || null;
        },
        activeDefaultJobId() {
            const targetJob = this.filters.job_id || this.$route.query.recruitment_job_id || this.$route.query.job_id;
            if (targetJob) {
                const filterStr = String(targetJob);
                const queryOrderId = this.$route.query.order_id ? String(this.$route.query.order_id) : "";
                const found = (this.roOrders || []).find((o) =>
                    (queryOrderId && String(o.id) === queryOrderId) ||
                    (o.recruitment_job_id && String(o.recruitment_job_id) === filterStr) ||
                    String(o.id) === filterStr ||
                    (o.job_id && String(o.job_id) === filterStr)
                );
                if (found) {
                    const rJobId = (found.recruitment_job_id !== undefined && found.recruitment_job_id !== null && found.recruitment_job_id !== "")
                        ? found.recruitment_job_id
                        : found.id;
                    return isNaN(Number(rJobId)) ? rJobId : Number(rJobId);
                }
                return isNaN(Number(targetJob)) ? targetJob : Number(targetJob);
            }
            return null;
        },
        displayedCvs() {
            return (this.rcvList || []).map((c) => {
                if (c.order_title) return c;
                const found = (this.roOrders || []).find((o) =>
                    String(o.id) === String(c.job_id) ||
                    String(o.id) === String(c.order_id) ||
                    (o.recruitment_job_id && String(o.recruitment_job_id) === String(c.job_id))
                );
                if (found) {
                    return {
                        ...c,
                        order_title: found.title || found.position_title || "",
                    };
                }
                return c;
            });
        },
        totalFilteredCount() {
            return (this.rcvPagination && this.rcvPagination.total !== undefined)
                ? Number(this.rcvPagination.total)
                : (this.rcvList || []).length;
        },
        totalCount() {
            return (this.rcvPagination && this.rcvPagination.total !== undefined)
                ? Number(this.rcvPagination.total)
                : (this.rcvList || []).length;
        },
        pendingReviewCount() {
            return (this.rcvList || []).filter(
                (c) => Number(c.status) === HR_CAN_DUYET || Number(c.status) === TEAM_LEAD_CAN_DUYET
            ).length;
        },
        interviewingCount() {
            const interviewStatuses = [
                PHONG_VAN_V1,
                PHONG_VAN_V2,
                DA_NHAP_KET_QUA_PHONG_VAN_V1_PASS,
                DA_NHAP_KET_QUA_PHONG_VAN_V2_PASS,
            ];
            return (this.rcvList || []).filter((c) => interviewStatuses.includes(Number(c.status))).length;
        },
        hiredCount() {
            return (this.rcvList || []).filter((c) => Number(c.status) === DA_NHAN_VIEC).length;
        },
    },
    watch: {
        "$route.query": {
            handler(query) {
                const val = (query && (query.recruitment_job_id || query.job_id)) || "";
                const newJobId = (val !== undefined && val !== null && val !== "")
                    ? (isNaN(Number(val)) ? val : Number(val))
                    : "";
                if (String(this.filters.job_id || "") !== String(newJobId || "")) {
                    this.filters.job_id = newJobId;
                    this.pagination.page = 1;
                    this.loadCvs();
                }
            },
        },
    },
    mounted() {
        this.$store.dispatch(SET_BREADCRUMB, [
            { title: "Hồ sơ ứng viên", icon: "fas fa-id-card" },
        ]);
        const initJob = this.$route.query.recruitment_job_id || this.$route.query.job_id;
        if (initJob !== undefined && initJob !== null && initJob !== "") {
            this.filters.job_id = isNaN(Number(initJob))
                ? initJob
                : Number(initJob);
        }
        this.loadCvStatus();
        this.loadOrders();
        this.loadCvs();
    },
    methods: {
        loadCvStatus() {
            return this.$store.dispatch(RCV_GET_CV_STATUS).catch(() => {});
        },
        getResolvedJobId(rawJobId) {
            if (rawJobId === "" || rawJobId === null || rawJobId === undefined) return undefined;
            const strVal = String(rawJobId);
            const order = (this.roOrders || []).find((o) =>
                String(o.id) === strVal ||
                (o.recruitment_job_id && String(o.recruitment_job_id) === strVal) ||
                (o.job_id && String(o.job_id) === strVal)
            );
            if (order && order.recruitment_job_id !== undefined && order.recruitment_job_id !== null && order.recruitment_job_id !== "") {
                return order.recruitment_job_id;
            }
            return rawJobId;
        },
        loadOrders() {
            return this.$store.dispatch(RO_GET_ORDERS, { limit: 100 })
                .then(() => {
                    const rawJob = this.filters.job_id || this.$route.query.recruitment_job_id || this.$route.query.job_id;
                    const resolved = this.getResolvedJobId(rawJob);
                    if (resolved && String(resolved) !== String(rawJob)) {
                        this.loadCvs();
                    }
                })
                .catch(() => {});
        },
        loadCvs() {
            const rawJob = (this.filters.job_id !== "" && this.filters.job_id !== null && this.filters.job_id !== undefined)
                ? this.filters.job_id
                : (this.$route.query.recruitment_job_id || this.$route.query.job_id);

            const targetJob = this.getResolvedJobId(rawJob);

            const params = {
                page: this.pagination.page,
                limit: this.pagination.limit,
            };

            if (targetJob !== "" && targetJob !== null && targetJob !== undefined) {
                params.job_id = targetJob;
            }
            if (this.filters.keyword && this.filters.keyword.trim()) {
                params.keyword = this.filters.keyword.trim();
            }
            if (this.filters.type !== "" && this.filters.type !== null && this.filters.type !== undefined) {
                params.type = this.filters.type;
            }
            if (this.filters.status !== "" && this.filters.status !== null && this.filters.status !== undefined) {
                params.status = this.filters.status;
            }
            if (this.filters.person_in_charge_id !== "" && this.filters.person_in_charge_id !== null && this.filters.person_in_charge_id !== undefined) {
                params.person_in_charge_id = this.filters.person_in_charge_id;
            }
            if (this.filters.teamlead_id !== "" && this.filters.teamlead_id !== null && this.filters.teamlead_id !== undefined) {
                params.teamlead_id = this.filters.teamlead_id;
            }
            if (this.filters.date_range && this.filters.date_range[0] && this.filters.date_range[1]) {
                params.start_date = this.filters.date_range[0];
                params.end_date = this.filters.date_range[1];
            }

            return this.$store.dispatch(RCV_GET_CVS, params).catch(() => {});
        },
        refreshData() {
            this.loadOrders();
            this.loadCvs();
        },
        clearJobFilter() {
            this.filters.job_id = "";
            this.pagination.page = 1;
            if (this.$route.query.job_id || this.$route.query.recruitment_job_id || this.$route.query.order_id) {
                const query = { ...this.$route.query };
                delete query.job_id;
                delete query.recruitment_job_id;
                delete query.order_id;
                this.$router.replace({ query }).catch(() => {});
            }
            this.loadCvs();
        },
        onFilterChange(newFilters) {
            this.pagination.page = 1;
            const currentQueryJob = this.$route.query.recruitment_job_id || this.$route.query.job_id;
            const newJob = newFilters && newFilters.job_id;
            if (newJob !== undefined && String(newJob || "") !== String(currentQueryJob || "")) {
                const query = { ...this.$route.query };
                if (newJob !== "" && newJob !== null && newJob !== undefined) {
                    query.job_id = String(newJob);
                    query.recruitment_job_id = String(newJob);
                } else {
                    delete query.job_id;
                    delete query.recruitment_job_id;
                    delete query.order_id;
                }
                this.$router.replace({ query }).catch(() => {});
            }
            this.loadCvs();
        },
        handlePageChange(page) {
            this.pagination.page = page;
            this.loadCvs();
        },
        handleSizeChange(limit) {
            this.pagination.limit = limit;
            this.pagination.page = 1;
            this.loadCvs();
        },
        openDetail(candidate) {
            this.selectedCandidate = candidate;
            this.detailInitialTab = "info";
            this.detailDialogVisible = true;
        },
        openPreviewCv(candidate) {
            this.selectedCandidate = candidate;
            this.detailInitialTab = "cv";
            this.detailDialogVisible = true;
        },
        openCreate() {
            this.selectedEditCandidate = null;
            this.formDialogVisible = true;
        },
        openEdit(candidate) {
            this.selectedEditCandidate = { ...candidate };
            this.formDialogVisible = true;
        },
        handleFormSubmit(payload) {
            this.submitting = true;
            const isEdit = !!payload.id;
            const action = isEdit ? RCV_UPDATE_CV : RCV_CREATE_CV;
            const successMsg = isEdit
                ? "Cập nhật hồ sơ ứng viên thành công"
                : "Thêm hồ sơ ứng viên thành công";

            const order = (this.roOrders || []).find((o) =>
                String(o.id) === String(payload.job_id) ||
                (o.recruitment_job_id && String(o.recruitment_job_id) === String(payload.job_id)) ||
                (payload.order_id && String(o.id) === String(payload.order_id)) ||
                (this.$route.query.order_id && String(o.id) === String(this.$route.query.order_id))
            );

            const recruitmentJobId = (order && order.recruitment_job_id !== undefined && order.recruitment_job_id !== null && order.recruitment_job_id !== "")
                ? order.recruitment_job_id
                : (payload.recruitment_job_id || payload.job_id);

            const submitPayload = {
                ...payload,
                job_id: recruitmentJobId,
                recruitment_job_id: recruitmentJobId,
                order_id: (order && order.id) || payload.order_id || payload.job_id,
                request_id: (order && order.id) || payload.request_id || payload.order_id || payload.job_id,
            };

            this.$store
                .dispatch(action, submitPayload)
                .then(() => {
                    this.$message.success(successMsg);
                    this.formDialogVisible = false;
                    this.loadCvs();
                    this.loadOrders();
                })
                .catch((err) => {
                    const fallbackMsg = isEdit ? "Cập nhật hồ sơ thất bại" : "Thêm hồ sơ thất bại";
                    const msg = this.extractApiErrorMessage(err, fallbackMsg);
                    this.$message.error(msg);
                })
                .finally(() => {
                    this.submitting = false;
                });
        },
        handleDelete(candidate) {
            const name = candidate.name || `#${candidate.id}`;
            this.$confirm(`Bạn có chắc chắn muốn xoá hồ sơ ứng viên "${name}"?`, "Xác nhận xoá", {
                confirmButtonText: "Xoá",
                cancelButtonText: "Huỷ",
                type: "warning",
            })
                .then(() => {
                    this.$store
                        .dispatch(RCV_DELETE_CV, candidate.id)
                        .then(() => {
                            this.$message.success("Xoá hồ sơ ứng viên thành công");
                            this.loadCvs();
                            this.loadOrders();
                        })
                        .catch(() => {
                            this.$message.error("Xoá hồ sơ thất bại");
                        });
                })
                .catch(() => {});
        },
        openSchedule({ candidate, round = 2 }) {
            this.selectedCandidate = candidate;
            this.detailInitialTab = "info";
            this.detailDialogVisible = true;
            this.$nextTick(() => {
                if (this.$refs.detailDialogRef) {
                    this.$refs.detailDialogRef.openInterviewScheduleDialog(round);
                }
            });
        },
        openOffer(candidate) {
            if (this.isHrDepartment) {
                this.$message.warning("Chỉ Teamlead mới có quyền điền thông tin Thử việc & Offer");
                return;
            }
            this.selectedOfferCandidate = candidate;
            this.offerProcedureDialogVisible = true;
        },
        handleOfferProcedureSubmit(payload) {
            if (this.isHrDepartment) {
                this.$message.warning("Chỉ Teamlead mới có quyền thực hiện thao tác này");
                return;
            }
            this.handleUpdateCandidateStatus(payload);
        },
        openWorkConfirmation({ candidate, hasWork = true } = {}) {
            this.selectedWorkCandidate = candidate;
            this.initialWorkStatus = hasWork;
            this.workConfirmationDialogVisible = true;
        },
        handleWorkConfirmationSubmit(payload) {
            this.handleUpdateCandidateStatus(payload);
            this.workConfirmationDialogVisible = false;
        },
        openTrainingEvaluation(candidate) {
            if (!this.isHrDepartment) {
                this.$message.warning("Chỉ HR mới có quyền nhập kết quả đào tạo hội nhập");
                return;
            }
            this.selectedTrainingCandidate = candidate;
            this.trainingEvaluationDialogVisible = true;
        },
        handleTrainingEvaluationSubmit(payload) {
            if (!this.isHrDepartment) {
                this.$message.warning("Chỉ HR mới có quyền thực hiện thao tác này");
                return;
            }
            this.handleUpdateCandidateStatus(payload);
            this.trainingEvaluationDialogVisible = false;
        },
        openInterview({ candidate, round = 1 }) {
            this.selectedInterviewCandidate = candidate;
            this.selectedInterviewRound = round;
            this.interviewDialogVisible = true;
        },
        handleInterviewSubmit(payload) {
            const isCancel = payload.result === "cancel";
            const targetId = payload.id;

            if (isCancel) {
                if (!this.isHrDepartment) {
                    this.$message.warning("Chỉ HR mới có quyền huỷ lịch phỏng vấn");
                    return;
                }
                const cancelPayload = {
                    id: targetId,
                    status: Number(payload.status),
                    round: payload.round,
                    cancel_reason: payload.cancel_reason,
                    reason: payload.reason || payload.cancel_reason,
                    note: payload.note,
                    candidate: payload.candidate,
                };
                this.handleUpdateCandidateStatus(cancelPayload);
                this.interviewDialogVisible = false;
                return;
            }

            this.statusSubmitting = true;
            const isPass = payload.result === "pass";

            // Chuẩn bị payload gửi tới API POST job-recruitment/cvs/{id}/status
            const statusPayload = {
                id: targetId,
                status: Number(payload.status),
                round: payload.round,
                result: payload.result,
                interview_result: payload.result,
                interview_round: payload.round,
                note: payload.note,
                message: payload.note,
                interview_note: payload.note,
                result_note: payload.result_note || payload.note,
                team_lead_feedback: payload.team_lead_feedback,
                interview_v1_note: payload.interview_v1_note,
                reason: (!isPass && payload.interview_data ? payload.interview_data.fail_reason : "") || payload.note,
                fail_reason: !isPass && payload.interview_data ? payload.interview_data.fail_reason : undefined,
                send_thank_you_mail: payload.send_thank_you_mail,
                ...(payload.date ? {
                    date: payload.date,
                    start_time: payload.start_time,
                    end_time: payload.end_time,
                } : {}),
                ...(payload.interview_data ? {
                    expected_salary: payload.interview_data.expected_salary,
                    salary: payload.interview_data.expected_salary,
                    start_date: payload.interview_data.start_date,
                    interview_data: payload.interview_data,
                } : {}),
                candidate: payload.candidate,
            };

            // Gọi API chính: POST job-recruitment/cvs/{id}/status
            const statusPromise = this.$store.dispatch(RCV_UPDATE_CV_STATUS, statusPayload);

            // Đồng bộ thêm RCV_UPDATE_CV nếu có cập nhật lương hoặc lịch phỏng vấn V2
            const updateCvPayload = {
                ...(payload.candidate || {}),
                id: targetId,
                status: Number(payload.status),
                team_lead_feedback: payload.team_lead_feedback || (payload.candidate && payload.candidate.team_lead_feedback),
                interview_v1_note: payload.interview_v1_note || (payload.candidate && payload.candidate.interview_v1_note),
                result_note: payload.result_note || (payload.candidate && payload.candidate.result_note),
                message: payload.candidate && payload.candidate.message
                    ? `${payload.candidate.message}\n${payload.note}`
                    : payload.note,
                ...(payload.interview_data && payload.interview_data.expected_salary ? {
                    salary: payload.interview_data.expected_salary,
                } : {}),
                ...(Number(payload.status) === PHONG_VAN_V2 && payload.date ? {
                    round: payload.round || 2,
                    date: payload.date,
                    start_time: payload.start_time,
                    end_time: payload.end_time,
                } : {}),
            };

            const updateCvPromise = (payload.date || (payload.interview_data && payload.interview_data.expected_salary))
                ? this.$store.dispatch(RCV_UPDATE_CV, updateCvPayload).catch(() => null)
                : Promise.resolve();

            Promise.all([statusPromise, updateCvPromise])
                .then(([res]) => {
                    if (isPass) {
                        let passMsg = "Đã lưu kết quả Đạt phỏng vấn và chuyển tiến trình ứng viên";
                        if (Number(payload.status) === PHONG_VAN_V2 && payload.date) {
                            passMsg = "Đã lưu kết quả Đạt V1 và xếp lịch Phỏng vấn Vòng 2 thành công";
                        } else if (Number(payload.status) === DA_NHAP_KET_QUA_PHONG_VAN_V1_PASS) {
                            passMsg = "Đã lưu kết quả Đạt PV Vòng 1 (Trạng thái: Pass V1). Vui lòng xếp lịch PV V2 sau.";
                        } else if (Number(payload.status) === CAN_DIEN_OFFER) {
                            passMsg = "Đã lưu kết quả Đạt PV Vòng 2 (Teamlead). Vui lòng nhập thông tin thử việc & offer.";
                        }
                        this.$message.success(passMsg);

                        // Sau khi điền kết quả pv lần 2 nếu nhập pass thì show dialog cho nhập: salary, probation_start_date, probation_end_date
                        if (Number(payload.round) === 2) {
                            const updatedCandidateData = {
                                ...(this.selectedInterviewCandidate || {}),
                                ...(this.selectedCandidate || {}),
                                ...updateCvPayload,
                                status: CAN_DIEN_OFFER,
                                ...(res && res.data ? res.data : {}),
                            };
                            this.selectedOfferCandidate = updatedCandidateData;
                            if (!this.isHrDepartment) {
                                this.offerProcedureDialogVisible = true;
                            }
                        }
                    } else {
                        this.$message.success("Đã lưu kết quả Không đạt và tự động gửi Mail Cảm ơn tới ứng viên");
                    }
                    this.interviewDialogVisible = false;

                    // Cập nhật selectedCandidate tức thời nếu đang mở detail dialog
                    if (this.selectedCandidate && String(this.selectedCandidate.id) === String(targetId)) {
                        this.selectedCandidate = {
                            ...this.selectedCandidate,
                            ...updateCvPayload,
                            status: Number(payload.status),
                            ...(res && res.data ? res.data : {}),
                        };
                    }
                    this.loadCvs();
                    this.loadOrders();
                })
                .catch((err) => {
                    const fallbackMsg = "Lưu kết quả phỏng vấn thất bại";
                    const msg = this.extractApiErrorMessage(err, fallbackMsg);
                    this.$message.error(msg);
                })
                .finally(() => {
                    this.statusSubmitting = false;
                });
        },
        handleCancelInterview({ candidate, round = 1, reason = "" }) {
            if (!this.isHrDepartment) {
                this.$message.warning("Chỉ HR mới có quyền huỷ lịch phỏng vấn");
                return;
            }
            const roundNum = Number(round) || 1;
            const targetStatus = roundNum === 2 ? HUY_PHONG_VAN_V2 : HUY_PHONG_VAN_V1;
            const roundName = roundNum === 2 ? "Vòng 2" : "Vòng 1";
            const candidateName = (candidate && candidate.name) || `#${(candidate && candidate.id) || ""}`;

            const doCancel = (cancelReason) => {
                const noteText = `[Huỷ lịch PV ${roundName}]: ${cancelReason}`;
                const payload = {
                    id: candidate.id,
                    status: targetStatus,
                    round: roundNum,
                    cancel_reason: cancelReason,
                    reason: cancelReason,
                    note: noteText,
                    candidate: {
                        ...candidate,
                        status: targetStatus,
                        cancel_reason: cancelReason,
                        ...(roundNum === 1
                            ? { cancel_interview_v1_reason: cancelReason }
                            : { cancel_interview_v2_reason: cancelReason }),
                    },
                };
                this.handleUpdateCandidateStatus(payload);
                this.interviewDialogVisible = false;
            };

            if (reason) {
                doCancel(reason);
                return;
            }

            this.$prompt(
                `Nhập lý do huỷ lịch tham gia phỏng vấn ${roundName} của ứng viên "${candidateName}":`,
                `Xác nhận huỷ lịch tham gia phỏng vấn ${roundName}`,
                {
                    confirmButtonText: "Xác nhận huỷ lịch",
                    cancelButtonText: "Đóng",
                    inputType: "textarea",
                    inputPlaceholder: "VD: Ứng viên bận việc đột xuất xin dời, Không liên lạc được, Ứng viên từ chối tham gia...",
                    inputPattern: /\S+/,
                    inputErrorMessage: "Vui lòng nhập lý do huỷ lịch phỏng vấn",
                    confirmButtonClass: "el-button--danger",
                }
            )
                .then(({ value }) => {
                    doCancel((value || "").trim());
                })
                .catch(() => {});
        },
        openProbation(candidate) {
            if (this.isHrDepartment) {
                this.$message.warning("Chỉ Teamlead mới có quyền điền kết quả thử việc");
                return;
            }
            this.selectedProbationCandidate = candidate;
            this.probationDialogVisible = true;
        },
        handleProbationSubmit(payload) {
            if (this.isHrDepartment) {
                this.$message.warning("Chỉ Teamlead mới có quyền thực hiện thao tác này");
                return;
            }
            this.statusSubmitting = true;
            const isPass = payload.result === "pass";
            const isEarlyExit = !isPass && Boolean(payload.probation_exit_date || payload.probation_exit_reason);
            const targetId = payload.id;

            const salaryVal = payload.official_salary !== undefined && payload.official_salary !== null
                ? payload.official_salary
                : null;
            const existingSalary = (payload.candidate && payload.candidate.salary) || payload.salary || null;

            const statusPayload = {
                id: targetId,
                status: Number(payload.status),
                result: payload.result,
                official_salary: salaryVal,
                ...(existingSalary !== null ? { salary: existingSalary } : {}),
                probation_date: payload.probation_date || null,
                probation_reason: payload.probation_reason || null,
                probation_exit_date: payload.probation_exit_date || null,
                probation_exit_reason: payload.probation_exit_reason || null,
                note: payload.note,
                message: payload.note,
                send_thank_you_mail: payload.send_thank_you_mail,
            };

            this.$store.dispatch(RCV_UPDATE_CV_STATUS, statusPayload)
                .then(() => {
                    if (isPass) {
                        this.$message.success("Đã lưu kết quả Đạt thử việc và cập nhật mức lương chính thức thành công");
                    } else {
                        this.$message.success("Đã ghi nhận kết quả Không đạt thử việc thành công");
                    }
                    this.probationDialogVisible = false;

                    // Cập nhật selectedCandidate tức thời nếu đang mở detail dialog
                    if (this.selectedCandidate && String(this.selectedCandidate.id) === String(targetId)) {
                        this.selectedCandidate = {
                            ...this.selectedCandidate,
                            status: Number(payload.status),
                            official_salary: salaryVal,
                            probation_result: payload.result,
                            probation_exit_date: payload.probation_exit_date || null,
                            probation_exit_reason: payload.probation_exit_reason || null,
                            probation_reason: payload.probation_reason || null,
                            probation_note: payload.note || (this.selectedCandidate && this.selectedCandidate.probation_note),
                        };
                    }
                    this.loadCvs();
                    this.loadOrders();
                })
                .catch((err) => {
                    const msg = this.extractApiErrorMessage(err, "Lưu kết quả đánh giá thử việc thất bại");
                    this.$message.error(msg);
                })
                .finally(() => {
                    this.statusSubmitting = false;
                });
        },
        handleMarkHired(candidate) {
            if (!this.isHrDepartment) {
                this.$message.warning("Chỉ HR mới có quyền cập nhật trạng thái Đã nhận việc");
                return;
            }
            const cand = candidate || this.selectedCandidate;
            if (!cand) return;
            const name = cand.name || cand.candidate_name || "ứng viên";
            this.$confirm(`Xác nhận ứng viên "${name}" ĐÃ NHẬN VIỆC chính thức?`, "Xác nhận Đã nhận việc", {
                confirmButtonText: "Xác nhận Đã nhận việc",
                cancelButtonText: "Huỷ",
                type: "success",
                confirmButtonClass: "el-button--success",
            })
                .then(() => {
                    this.handleUpdateCandidateStatus({
                        id: cand.id,
                        status: DA_NHAN_VIEC,
                        note: "[HR xác nhận]: Ứng viên đã nhận việc chính thức",
                        message: "[HR xác nhận]: Ứng viên đã nhận việc chính thức",
                        candidate: {
                            ...cand,
                            status: DA_NHAN_VIEC,
                        },
                    });
                })
                .catch(() => {});
        },
        extractApiErrorMessage(err, defaultMsg = "Đã có lỗi xảy ra") {
            if (!err) return defaultMsg;

            // 1. Lấy dữ liệu payload từ response hoặc data
            let payload = null;
            if (err.response && err.response.data !== undefined) {
                payload = err.response.data;
            } else if (err.data !== undefined) {
                payload = err.data;
            } else {
                payload = err;
            }

            // 2. Nếu payload là chuỗi thuần (text)
            if (typeof payload === "string" && payload.trim()) {
                const s = payload.trim();
                if (!s.startsWith("<")) {
                    return s;
                }
            }

            const fieldLabels = {
                name: "Họ và tên ứng viên",
                candidate_name: "Họ và tên ứng viên",
                phone: "Số điện thoại",
                candidate_phone: "Số điện thoại",
                email: "Email",
                candidate_email: "Email",
                address: "Địa chỉ",
                job_id: "Yêu cầu tuyển dụng",
                recruitment_job_id: "Yêu cầu tuyển dụng",
                request_id: "Yêu cầu tuyển dụng",
                order_id: "Yêu cầu tuyển dụng",
                source_type: "Nguồn CV",
                cv_file: "File CV",
                cv_files: "File CV",
                file: "File CV",
                files: "File CV",
                salary: "Mức lương (VNĐ)",
                official_salary: "Mức lương chính thức (VNĐ)",
                expected_salary: "Mức lương mong muốn (VNĐ)",
                probation_start_date: "Ngày thử việc (Bắt đầu)",
                probation_end_date: "Ngày kết thúc thử việc",
                start_date: "Ngày bắt đầu làm việc",
                probation_date: "Ngày thử việc",
                probation_reason: "Lý do ứng viên không đi làm",
            };

            const translateMessage = (msg, fieldKey = "") => {
                if (!msg) return "";
                if (typeof msg !== "string") {
                    if (Array.isArray(msg) && msg.length > 0) {
                        return translateMessage(msg[0], fieldKey);
                    }
                    return String(msg);
                }
                let clean = msg.trim();
                const fieldName = fieldLabels[fieldKey] || (fieldKey ? fieldKey : "");

                if (/The (.+) field is required/i.test(clean) || /(.+) is required/i.test(clean)) {
                    const match = clean.match(/The (.+) field is required/i) || clean.match(/(.+) is required/i);
                    const rawF = match && match[1] ? match[1].trim() : "";
                    const f = fieldLabels[rawF] || fieldLabels[rawF.replace(/ /g, "_")] || fieldName || rawF;
                    return `Vui lòng nhập/chọn ${f}`;
                }
                if (/Trường (.+) là bắt buộc/i.test(clean)) {
                    const match = clean.match(/Trường (.+) là bắt buộc/i);
                    const rawF = match && match[1] ? match[1].trim() : "";
                    const f = fieldLabels[rawF] || fieldLabels[rawF.replace(/ /g, "_")] || fieldName || rawF;
                    return `Vui lòng nhập/chọn ${f}`;
                }

                Object.keys(fieldLabels).forEach((k) => {
                    const reg = new RegExp(`\\b${k}\\b`, "gi");
                    clean = clean.replace(reg, fieldLabels[k]);
                });
                return clean;
            };

            if (payload && typeof payload === "object") {
                // 1. Kiểm tra message_validate_form hoặc errors (ở payload hoặc payload.data)
                const mv =
                    (payload.data && (payload.data.message_validate_form || payload.data.errors)) ||
                    payload.message_validate_form ||
                    payload.errors;

                if (mv && typeof mv === "object") {
                    const keys = Object.keys(mv);
                    if (keys.length > 0) {
                        const firstKey = keys[0];
                        const firstVal = mv[firstKey];
                        const resMsg = translateMessage(firstVal, firstKey);
                        if (resMsg) return resMsg;
                    }
                }

                // 2. Kiểm tra message / msg trực tiếp từ payload hoặc payload.data
                const directMessages = [
                    payload.message,
                    payload.msg,
                    payload.error_message,
                    payload.data && typeof payload.data === "object" ? payload.data.message : null,
                    payload.data && typeof payload.data === "object" ? payload.data.msg : null,
                    payload.data && typeof payload.data === "object" ? payload.data.error_message : null,
                ];

                for (const m of directMessages) {
                    if (m && typeof m === "string" && m.trim()) {
                        if (m.toLowerCase().includes("the given data was invalid") && mv) {
                            continue;
                        }
                        return translateMessage(m);
                    }
                }

                // 3. Kiểm tra error nếu error là chuỗi
                if (typeof payload.error === "string" && payload.error.trim()) {
                    return translateMessage(payload.error);
                }
                if (payload.data && typeof payload.data.error === "string" && payload.data.error.trim()) {
                    return translateMessage(payload.data.error);
                }

                // 4. Nếu payload.data là string trực tiếp
                if (typeof payload.data === "string" && payload.data.trim() && !payload.data.trim().startsWith("<")) {
                    return payload.data.trim();
                }
            }

            if (typeof err === "string" && err.trim()) return translateMessage(err);
            if (err.message && typeof err.message === "string" && err.message.trim()) {
                return translateMessage(err.message);
            }
            return defaultMsg;
        },
        handleUpdateCandidateStatus(statusPayload) {
            const isCancel =
                Number(statusPayload.status) === HUY_PHONG_VAN_V1 ||
                Number(statusPayload.status) === HUY_PHONG_VAN_V2;

            if (isCancel && !this.isHrDepartment) {
                this.$message.warning("Chỉ HR mới có quyền huỷ lịch phỏng vấn");
                return;
            }

            if (Number(statusPayload.status) === DA_NHAN_VIEC && !this.isHrDepartment) {
                this.$message.warning("Chỉ HR mới có quyền cập nhật trạng thái Đã nhận việc");
                return;
            }

            this.statusSubmitting = true;
            const targetId = statusPayload.id || (statusPayload.candidate && statusPayload.candidate.id);

            // Gọi API POST job-recruitment/cvs/{id}/status
            const statusPromise = this.$store.dispatch(RCV_UPDATE_CV_STATUS, statusPayload);

            // Chỉ cập nhật bổ sung hồ sơ khi statusPayload có chứa thông tin lương/ngày thử việc/lý do thử việc mới
            const hasSalaryOrStartDate = Boolean(
                statusPayload.salary ||
                statusPayload.official_salary ||
                statusPayload.start_date ||
                statusPayload.probation_start_date ||
                statusPayload.probation_end_date ||
                statusPayload.probation_reason
            );

            const updateCvPayload = {
                id: targetId,
                ...(statusPayload.candidate || {}),
                status: Number(statusPayload.status),
                ...(statusPayload.date ? {
                    round: statusPayload.round,
                    date: statusPayload.date,
                    start_time: statusPayload.start_time,
                    end_time: statusPayload.end_time,
                } : {}),
                ...(hasSalaryOrStartDate ? {
                    salary: statusPayload.salary || (statusPayload.candidate && statusPayload.candidate.salary),
                    ...(statusPayload.official_salary !== undefined ? { official_salary: statusPayload.official_salary } : {}),
                    start_date: statusPayload.start_date || statusPayload.probation_start_date || (statusPayload.candidate && (statusPayload.candidate.start_date || statusPayload.candidate.probation_start_date)),
                    probation_start_date: statusPayload.probation_start_date || statusPayload.start_date || (statusPayload.candidate && (statusPayload.candidate.probation_start_date || statusPayload.candidate.start_date)),
                    probation_end_date: statusPayload.probation_end_date || (statusPayload.candidate && statusPayload.candidate.probation_end_date),
                    probation_date: statusPayload.probation_start_date || statusPayload.start_date || (statusPayload.candidate && (statusPayload.candidate.probation_start_date || statusPayload.candidate.probation_date)),
                    offer_note: statusPayload.offer_note || (statusPayload.candidate && statusPayload.candidate.offer_note),
                    probation_reason: statusPayload.probation_reason !== undefined ? statusPayload.probation_reason : (statusPayload.candidate && statusPayload.candidate.probation_reason),
                } : {}),
            };

            const updateCvPromise = (statusPayload.date || hasSalaryOrStartDate)
                ? this.$store.dispatch(RCV_UPDATE_CV, updateCvPayload).catch(() => null)
                : Promise.resolve();

            Promise.all([statusPromise, updateCvPromise])
                .then(([res]) => {
                    const isCancel =
                        Number(statusPayload.status) === HUY_PHONG_VAN_V1 ||
                        Number(statusPayload.status) === HUY_PHONG_VAN_V2;
                    const isReject =
                        Number(statusPayload.status) === HR_TU_CHOI ||
                        Number(statusPayload.status) === TEAM_LEAD_TU_CHOI ||
                        statusPayload.hr_approve === 2 ||
                        statusPayload.teamlead_approve === 2;

                    let successMsg = "Cập nhật trạng thái duyệt hồ sơ thành công";
                    if (isCancel) {
                        successMsg = "Đã huỷ lịch tham gia phỏng vấn của ứng viên";
                    } else if (isReject) {
                        successMsg = "Từ chối hồ sơ ứng viên thành công";
                    } else if (Number(statusPayload.status) === CAN_LAM_THU_TUC_PASS) {
                        successMsg = "Đã điền thông tin Offer và chuyển sang Cần làm thủ tục Pass (#14) thành công";
                    } else if (Number(statusPayload.status) === UNG_VIEN_KHONG_DI_LAM) {
                        successMsg = "Đã cập nhật trạng thái: Ứng viên không đi làm (#15)";
                    } else if (Number(statusPayload.status) === DA_GUI_DAO_TAO_HOI_NHAP) {
                        successMsg = "Đã xác nhận ứng viên có đi làm và chuyển sang Đã gửi đào tạo hội nhập (#16) thành công";
                    } else if (Number(statusPayload.status) === CAN_DANH_GIA_THU_VIEC) {
                        successMsg = "Đã lưu kết quả Đào tạo hội nhập và chuyển sang Cần đánh giá thử việc (#22) thành công";
                    } else if (Number(statusPayload.status) === DA_NHAN_VIEC) {
                        successMsg = "Đã cập nhật trạng thái: Đã nhận việc (#25) thành công";
                    } else if (hasSalaryOrStartDate) {
                        successMsg = "Đã cập nhật thông tin Offer thành công";
                    } else if (statusPayload.date) {
                        const rName = Number(statusPayload.round) === 2 ? "Vòng 2" : "Vòng 1";
                        successMsg = `Đã xếp lịch phỏng vấn ${rName} thành công`;
                    }
                    this.$message.success(successMsg);

                    // Cập nhật selectedCandidate tức thời để dialog hiển thị ngay tiến trình mới
                    if (this.selectedCandidate && String(this.selectedCandidate.id) === String(targetId)) {
                        this.selectedCandidate = {
                            ...this.selectedCandidate,
                            ...(res && res.data && typeof res.data === "object" ? res.data : {}),
                            ...statusPayload,
                            status: Number(statusPayload.status),
                        };
                    }
                    this.$store.commit(RCV_UPSERT_CV, {
                        ...(this.selectedCandidate || {}),
                        id: targetId,
                        status: Number(statusPayload.status),
                        status_text: STATUS_TEXT[Number(statusPayload.status)],
                    });
                    this.loadCvs();
                    this.loadOrders();
                })
                .catch((err) => {
                    const fallbackMsg = "Cập nhật tiến trình duyệt CV thất bại";
                    const msg = this.extractApiErrorMessage(err, fallbackMsg);
                    this.$message.error(msg);
                })
                .finally(() => {
                    this.statusSubmitting = false;
                });
        },
        openPreliminary(candidate) {
            this.selectedPreliminaryCandidate = candidate;
            this.preliminaryDialogVisible = true;
        },
        handlePreliminarySubmit(payload) {
            this.statusSubmitting = true;
            const targetId = payload.id || (payload.candidate && payload.candidate.id);

            this.$store
                .dispatch(RCV_UPDATE_CV_STATUS, payload)
                .then((res) => {
                    this.$message.success("Gửi duyệt CV và chuyển Teamlead duyệt thành công");
                    this.preliminaryDialogVisible = false;

                    // Cập nhật selectedCandidate tức thời để modal chi tiết hiển thị dữ liệu mới
                    if (this.selectedCandidate && String(this.selectedCandidate.id) === String(targetId)) {
                        this.selectedCandidate = {
                            ...this.selectedCandidate,
                            ...payload,
                            ...(res && res.data ? res.data : {}),
                        };
                    }
                    this.loadCvs();
                    this.loadOrders();
                })
                .catch((err) => {
                    const fallbackMsg = "Gửi duyệt CV thất bại";
                    const msg = this.extractApiErrorMessage(err, fallbackMsg);
                    this.$message.error(msg);
                })
                .finally(() => {
                    this.statusSubmitting = false;
                });
        },
    },
};
</script>

<style scoped>
.rcv-page {
    padding: 4px 2px 24px;
}
.rcv-toolbar {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    align-items: center;
    justify-content: space-between;
    background: #fff;
    border: 1px solid #eef0f5;
    border-radius: 14px;
    padding: 14px 18px;
    margin-bottom: 14px;
}
.rcv-toolbar__left {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
}
.rcv-toolbar__title {
    margin: 0;
    font-size: 18px;
    font-weight: 700;
    color: #1f2430;
    display: flex;
    align-items: center;
    gap: 8px;
}
.rcv-toolbar__title i {
    color: #4f46e5;
}
.rcv-toolbar__sub {
    font-size: 13px;
    color: #64748b;
}
.rcv-active-job-tag {
    font-weight: 600;
    border-radius: 6px;
    font-size: 12px;
}
.rcv-toolbar__right {
    display: flex;
    align-items: center;
    gap: 8px;
}
.rcv-stats {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
    margin-bottom: 14px;
}
@media (max-width: 992px) {
    .rcv-stats {
        grid-template-columns: repeat(2, 1fr);
    }
}
@media (max-width: 576px) {
    .rcv-stats {
        grid-template-columns: 1fr;
    }
}
.rcv-stat-card {
    background: #fff;
    border: 1px solid #eef0f5;
    border-radius: 12px;
    padding: 14px 16px;
    display: flex;
    align-items: center;
    gap: 14px;
}
.rcv-stat-card__icon {
    width: 44px;
    height: 44px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
}
.rcv-stat-card__icon--primary {
    background: #eef2ff;
    color: #4f46e5;
}
.rcv-stat-card__icon--success {
    background: #ecfdf5;
    color: #059669;
}
.rcv-stat-card__icon--info {
    background: #f0f9ff;
    color: #0284c7;
}
.rcv-stat-card__icon--warning {
    background: #fef3c7;
    color: #b45309;
}
.rcv-stat-card__info {
    display: flex;
    flex-direction: column;
}
.rcv-stat-card__value {
    font-size: 20px;
    font-weight: 800;
    color: #1e293b;
    line-height: 1.2;
}
.rcv-stat-card__label {
    font-size: 12px;
    color: #64748b;
    margin-top: 2px;
}
.rcv-card {
    background: #fff;
    border: 1px solid #eef0f5;
    border-radius: 12px;
    padding: 16px;
    margin-bottom: 14px;
}
.rcv-filter-card {
    padding: 12px 16px;
}
.rcv-pagination {
    display: flex;
    justify-content: flex-end;
    margin-top: 16px;
    padding-top: 12px;
    border-top: 1px solid #f3f4f6;
}
.rcv-pagination /deep/ .el-pagination,
.rcv-pagination ::v-deep .el-pagination {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    font-weight: normal;
    color: #64748b;
}
.rcv-pagination /deep/ .el-pagination > span,
.rcv-pagination /deep/ .el-pagination > button,
.rcv-pagination /deep/ .el-pagination > ul,
.rcv-pagination ::v-deep .el-pagination > span,
.rcv-pagination ::v-deep .el-pagination > button,
.rcv-pagination ::v-deep .el-pagination > ul {
    display: inline-flex;
    align-items: center;
    height: 28px;
    line-height: 28px;
    vertical-align: middle;
}
.rcv-pagination /deep/ .el-pagination .el-pagination__total,
.rcv-pagination ::v-deep .el-pagination .el-pagination__total {
    margin-right: 8px;
    height: 28px;
    line-height: 28px;
    display: inline-flex;
    align-items: center;
}
.rcv-pagination /deep/ .el-pagination .el-pagination__sizes,
.rcv-pagination ::v-deep .el-pagination .el-pagination__sizes {
    margin-right: 8px;
    height: 28px;
    display: inline-flex;
    align-items: center;
}
.rcv-pagination /deep/ .el-pagination .el-pagination__sizes .el-input,
.rcv-pagination ::v-deep .el-pagination .el-pagination__sizes .el-input {
    margin: 0;
    vertical-align: middle;
}
.rcv-pagination /deep/ .el-pagination .el-pagination__sizes .el-input .el-input__inner,
.rcv-pagination ::v-deep .el-pagination .el-pagination__sizes .el-input .el-input__inner {
    height: 28px;
    line-height: 28px;
}
.rcv-pagination /deep/ .el-pagination .btn-prev,
.rcv-pagination /deep/ .el-pagination .btn-next,
.rcv-pagination ::v-deep .el-pagination .btn-prev,
.rcv-pagination ::v-deep .el-pagination .btn-next {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 28px;
    line-height: 28px;
    padding: 0 4px;
    margin: 0 2px;
}
.rcv-pagination /deep/ .el-pagination .el-pager,
.rcv-pagination ::v-deep .el-pagination .el-pager {
    display: inline-flex;
    align-items: center;
    height: 28px;
    line-height: 28px;
    padding: 0;
    margin: 0;
}
.rcv-pagination /deep/ .el-pager li,
.rcv-pagination ::v-deep .el-pager li {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 28px;
    line-height: 28px;
    min-width: 28px;
    margin: 0 2px;
    font-size: 13px;
    border-radius: 4px;
}
.rcv-pagination /deep/ .el-pagination .el-pagination__jump,
.rcv-pagination ::v-deep .el-pagination .el-pagination__jump {
    display: inline-flex;
    align-items: center;
    height: 28px;
    line-height: 28px;
    margin-left: 10px;
    color: #64748b;
}
.rcv-pagination /deep/ .el-pagination .el-pagination__jump .el-pagination__editor.el-input,
.rcv-pagination ::v-deep .el-pagination .el-pagination__jump .el-pagination__editor.el-input {
    margin: 0 4px;
    vertical-align: middle;
}
.rcv-pagination /deep/ .el-pagination .el-pagination__jump .el-input__inner,
.rcv-pagination ::v-deep .el-pagination .el-pagination__jump .el-input__inner {
    height: 28px;
    line-height: 28px;
}

</style>
